import React from 'react';
import { ChatConversation } from '../../types';

interface Props {
  conversations: ChatConversation[];
  selectedId: string | null;
  currentUserId: string;
  onSelect: (id: string) => void;
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T'));
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

const ConversationList: React.FC<Props> = ({ conversations, selectedId, currentUserId, onSelect }) => {
  return (
    <div className="flex-1 overflow-y-auto">
      {conversations.map(c => {
        // Pour une conv directe, afficher le nom de l'AUTRE participant
        // (et non "Direct" générique).
        let title = c.name || 'Sans nom';
        if (c.type === 'direct') {
          const other = c.participants.find(p => p.user_id !== currentUserId && p.role === 'member');
          title = other?.user_name || 'Conversation';
        }

        const observerCount = c.participants.filter(p => p.role === 'observer').length;

        return (
          <button
            key={c.id}
            type="button"
            onClick={() => onSelect(c.id)}
            className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-[rgba(100,140,210,0.1)] hover:bg-slate-100 dark:hover:bg-[rgba(100,140,210,0.08)] transition-colors ${
              selectedId === c.id ? 'bg-slate-200/60 dark:bg-[rgba(100,140,210,0.12)]' : ''
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-900 dark:text-[#e0eaff] truncate">{title}</span>
                  {c.type === 'group' && (
                    <span className="text-[9px] text-slate-400 dark:text-[#546a96] font-mono">GROUPE</span>
                  )}
                  {c.is_private && (
                    <span title="Conversation privée — pas d'observateurs" className="text-[10px]">🔒</span>
                  )}
                  {observerCount > 0 && !c.is_private && (
                    <span title={`${observerCount} observateur(s)`} className="text-[10px]">👁️</span>
                  )}
                </div>
                {c.last_message && (
                  <p className="text-xs text-slate-500 dark:text-[#7f93bc] mt-0.5 truncate">
                    {c.last_message.sender_id === currentUserId && <span className="text-slate-400 dark:text-[#546a96]">Vous : </span>}
                    {c.last_message.content}
                  </p>
                )}
              </div>

              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-[10px] text-slate-400 dark:text-[#546a96]">{formatTime(c.last_message_at)}</span>
                {c.unread_count > 0 && (
                  <span className="text-[10px] font-bold bg-cyan-500 text-white rounded-full px-2 py-0.5 min-w-[20px] text-center">
                    {c.unread_count > 99 ? '99+' : c.unread_count}
                  </span>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default ConversationList;
